import React, { useEffect, useMemo } from 'react';

interface MetaPixelProps {
  /**
   * One pixel ID, or several to fire in parallel — either as an array or as a
   * comma-separated string, so a migration can be driven from an env var
   * without a deploy. Every ID gets its own `init`; `track` calls then fan out
   * to all of them, which is how you move pixels without a gap in either.
   */
  id: string | string[];
}

// Minimal shape of the global `fbq` stub created before fbevents.js loads.
interface FbqStub {
  // eslint-disable-next-line no-unused-vars
  (..._args: unknown[]): void;
  // eslint-disable-next-line no-unused-vars
  callMethod?: (..._args: unknown[]) => void;
  queue: unknown[][];
  push: FbqStub;
  loaded: boolean;
  version: string;
}

// Detect react-snap's puppeteer UA so the pixel isn't injected into the
// prerendered HTML (it would otherwise double-fire when the page hydrates).
const IS_PRERENDERING =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const FBEVENTS_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

/**
 * The React equivalent of Meta's copy-paste base code: same `fbq` stub, same
 * `init` + `PageView`, same immediate fbevents.js load, same `<noscript>`
 * beacon — just driven from a component so the IDs can come from config and a
 * migration can run two pixels at once.
 *
 * The script load used to be deferred off the critical path (idle callback,
 * first interaction, or a 3s timeout) for speed. That's been dropped: it left a
 * window of up to three seconds at the start of every visit where a bounce sent
 * nothing at all, and on paid traffic those are exactly the visits you can least
 * afford to lose. Reliable attribution beats the few milliseconds saved.
 *
 * Conversions are reported via `fbq('track', 'Lead')` from trackGenerateLead()
 * in src/lib/utils/dataLayer.ts, so every lead path (contact form, both
 * landing pages, AI calls) is covered from a single place without double-firing.
 */
export const MetaPixel: React.FC<MetaPixelProps> = ({ id }) => {
  // Collapse to a stable primitive so an inline array literal can't re-run the
  // effect on every render and queue a duplicate PageView per pixel.
  const idKey = Array.isArray(id) ? id.join(',') : id;
  const ids = useMemo(
    () =>
      idKey
        .split(',')
        .map(value => value.trim())
        .filter(Boolean),
    [idKey]
  );

  useEffect(() => {
    // Don't execute if there are no usable IDs, or during prerender.
    if (ids.length === 0 || IS_PRERENDERING) {
      return;
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const w = window as unknown as {
      fbq?: FbqStub;
      _fbq?: FbqStub;
    };

    // Create the fbq stub once (queues calls until fbevents.js is loaded).
    if (!w.fbq) {
      const stub = function (...args: unknown[]) {
        if (stub.callMethod) {
          stub.callMethod(...args);
        } else {
          stub.queue.push(args);
        }
      } as FbqStub;
      stub.queue = [];
      stub.push = stub;
      stub.loaded = true;
      stub.version = '2.0';
      w.fbq = stub;
      w._fbq = w._fbq || stub;
    }

    const fbq = w.fbq as FbqStub;
    // Init every pixel first, then track once — fbevents fans a single `track`
    // out to all initialised pixels, so this is one PageView each, not one per
    // pixel per pixel.
    ids.forEach(pixelId => fbq('init', pixelId));
    fbq('track', 'PageView');

    // Load fbevents.js straight away, exactly as Meta's base code does, so the
    // queued PageView is transmitted rather than sitting in the queue until the
    // visitor happens to interact.
    try {
      // Guard against double-injection.
      if (!document.querySelector(`script[src^="${FBEVENTS_SRC}"]`)) {
        const script = document.createElement('script');
        script.async = true;
        script.src = FBEVENTS_SRC;
        script.onerror = () => {
          console.warn('Meta Pixel script failed to load');
        };
        document.head.appendChild(script);
      }
    } catch (e) {
      console.warn('Meta Pixel initialization failed:', e);
    }
  }, [ids]);

  // No <noscript> beacon, unlike Meta's copy-paste snippet. It was tried and
  // removed: React only renders it after hydration, by which point scripting is
  // by definition enabled, and react-snap drops it from the prerendered HTML —
  // measured as zero occurrences in every built .html file and zero in the live
  // DOM. In this architecture it can never fire, so it would be decoration.
  return null;
};

export default MetaPixel;
